/**
 * TranslateWidget — free, whole-page translation via the Google
 * Translate Element library. Renders a single, prominent unified
 * ``Translate to ▾`` dropdown (per Q5.b of the design brief) that
 * covers everything on the current page in one click.
 *
 * Why Google Translate Element (and not our LLM-based
 * ``/api/i18n/translate``)?
 *   - Zero LLM cost — the LLM path was ~50–150 calls per page.
 *   - Whole-page in a single browser call, including chart labels and
 *     dynamically-injected DOM.
 *   - Cancel button restores the DOM to the original English text.
 *
 * Loading strategy: the widget lazy-loads the Google script the FIRST
 * time the component mounts anywhere in the app, then reuses the
 * cached script for every subsequent mount. A hidden ``<div id="gt_root"
 * />`` receives the Google-injected combobox, but we hide it with CSS
 * because our own dropdown drives the translation via a Google-Translate
 * cookie (``googtrans``). Removing that cookie + reloading resets the
 * page to English.
 */
import { useEffect, useMemo, useState } from "react";
import { Globe, X, Languages } from "lucide-react";

const LANGUAGES = [
  { code: "en",     label: "English"    },
  { code: "es",     label: "Español"    },
  { code: "hi",     label: "हिन्दी"     },
  { code: "ta",     label: "தமிழ்"      },
  { code: "ml",     label: "മലയാളം"   },
  { code: "te",     label: "తెలుగు"    },
  { code: "kn",     label: "ಕನ್ನಡ"     },
  { code: "mr",     label: "मराठी"     },
  { code: "bn",     label: "বাংলা"     },
  { code: "gu",     label: "ગુજરાતી"    },
  { code: "pa",     label: "ਪੰਜਾਬੀ"    },
  { code: "ur",     label: "اردو"      },
  { code: "ar",     label: "العربية"   },
  { code: "fr",     label: "Français"   },
  { code: "de",     label: "Deutsch"    },
  { code: "pt",     label: "Português"  },
  { code: "ru",     label: "Русский"    },
  { code: "zh-CN",  label: "中文 (简体)"  },
  { code: "ja",     label: "日本語"      },
  { code: "ko",     label: "한국어"      },
  { code: "it",     label: "Italiano"   },
  { code: "nl",     label: "Nederlands" },
  { code: "tr",     label: "Türkçe"     },
  { code: "vi",     label: "Tiếng Việt" },
  { code: "th",     label: "ภาษาไทย"   },
  { code: "id",     label: "Bahasa Indonesia" },
];

const GT_SCRIPT_ID = "google-translate-script";
const GT_ROOT_ID   = "gt_widget_root";
const COOKIE_NAME  = "googtrans";
const AUTO_DETECT_FLAG = "sp:auto-translate-ran";   // localStorage flag — one-shot

/** Read the currently-active target language from the googtrans cookie. */
function currentTargetFromCookie() {
  const raw = (typeof document !== "undefined" ? document.cookie : "") || "";
  const match = raw.split("; ").find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return "en";
  // Cookie format: "/en/<target>" — e.g. "/en/hi" for Hindi.
  const parts = match.split("=")[1].split("/");
  return parts[2] || "en";
}

/** Set the googtrans cookie (both host- and domain-scoped, per Google spec). */
function setGoogTransCookie(target) {
  if (typeof document === "undefined") return;
  const value = `/en/${target}`;
  // Host cookie
  document.cookie = `${COOKIE_NAME}=${value}; path=/;`;
  // Also set on parent domain so subdomains (kerala./foodpulse./…) share it.
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length >= 2) {
    const parent = "." + parts.slice(-2).join(".");
    document.cookie = `${COOKIE_NAME}=${value}; domain=${parent}; path=/;`;
  }
}

/** Wipe the googtrans cookie. Google reads it on next page load. */
function clearGoogTransCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length >= 2) {
    const parent = "." + parts.slice(-2).join(".");
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${parent}; path=/;`;
  }
}

// Detect browser language on first visit. Returns a supported LANGUAGES
// code, or "" if user is English / already ran auto-detect / no match.
function detectBrowserLang() {
  try {
    if (localStorage.getItem(AUTO_DETECT_FLAG)) return "";
    const raw = (navigator.language || navigator.userLanguage || "").toLowerCase();
    const primary = raw.split("-")[0];
    if (!primary || primary === "en") return "";
    if (primary === "zh") return LANGUAGES.find((l) => l.code === "zh-CN") ? "zh-CN" : "";
    return LANGUAGES.find((l) => l.code === primary)?.code || "";
  } catch { return ""; }
}

/** Idempotently inject the Google Translate script + init callback. */
function ensureGoogleTranslateLoaded() {
  if (typeof window === "undefined") return;
  if (document.getElementById(GT_SCRIPT_ID)) return;

  // Google's element.js expects a global init function to be present.
  window.googleTranslateElementInit = function () {
    /* global google */
    if (!window.google || !window.google.translate) return;
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "en",
        // Include the whole superset so users can switch to any of them.
        includedLanguages: LANGUAGES.filter((l) => l.code !== "en").map((l) => l.code).join(","),
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      },
      GT_ROOT_ID,
    );
  };

  const s = document.createElement("script");
  s.id  = GT_SCRIPT_ID;
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

export default function TranslateWidget({ className = "", compact = false, dark = false }) {
  const [target, setTarget] = useState(() => currentTargetFromCookie());
  const [autoBanner, setAutoBanner] = useState(false);

  useEffect(() => { ensureGoogleTranslateLoaded(); }, []);

  // First-visit auto-detect: if user has no cookie yet AND browser language
  // is supported non-English AND we haven't already tried, silently set the
  // cookie and reload so Google Translate picks it up. Show a small banner
  // afterwards so users know how to revert to English.
  useEffect(() => {
    if (target && target !== "en") return;                     // already translated
    const detected = detectBrowserLang();
    if (!detected) return;
    try { localStorage.setItem(AUTO_DETECT_FLAG, "1"); } catch {}
    setGoogTransCookie(detected);
    // Instead of a hard reload, tell the user we detected + let them choose:
    setTarget(detected);
    setAutoBanner(true);
    // Google Translate needs a page repaint to activate — the cookie is now
    // set, so on the next navigation or manual refresh it kicks in.
    // For instant activation, reload once.
    setTimeout(() => { try { window.location.reload(); } catch {} }, 150);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const label = useMemo(
    () => LANGUAGES.find((l) => l.code === target)?.label || "English",
    [target],
  );

  const applyTarget = (code) => {
    setTarget(code);
    if (code === "en") {
      clearGoogTransCookie();
    } else {
      setGoogTransCookie(code);
    }
    // Google reads the cookie on load — reload once to apply page-wide.
    // This is the officially-supported invocation pattern for the element.
    window.location.reload();
  };

  const cancel = () => applyTarget("en");
  const active = target !== "en";
  // Dark-mode styling for the parent bar (higher-contrast pill).
  const isDark = dark || (className || "").includes("translate-widget-dark");

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="translate-widget">
      {/* The hidden mount point Google needs. We show our own combobox instead. */}
      <div id={GT_ROOT_ID} style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true" />

      <label className="inline-flex items-center gap-1.5 text-xs">
        {compact
          ? <Globe className={`w-3.5 h-3.5 ${isDark ? "opacity-90" : "opacity-70"}`} aria-hidden />
          : <><Languages className={`w-4 h-4 ${isDark ? "opacity-90" : "opacity-80"}`} aria-hidden />
              <span className={`font-mono uppercase tracking-wider text-[10px] ${isDark ? "opacity-90" : "opacity-70"}`}>Translate to</span></>
        }
        <select
          value={target}
          onChange={(e) => applyTarget(e.target.value)}
          className={`px-3 py-1.5 rounded-full text-sm font-semibold ${isDark ? "border-2" : "border"}`}
          style={{
            background: isDark ? "#FDFBF7" : "white",
            color: "#1F2937",
            borderColor: isDark ? "#FDBA74" : "var(--wofy-border)",
          }}
          data-testid="translate-widget-select"
          aria-label="Translate page to"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </label>

      {active && (
        <button
          type="button"
          onClick={cancel}
          className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full ${isDark ? "border border-white/40 hover:bg-white/10" : "border hover:bg-black/5"}`}
          style={isDark ? { color: "#FDFBF7" } : { borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
          data-testid="translate-widget-cancel"
          title="Reset the page to English"
        >
          <X className="w-3 h-3" /> Cancel
        </button>
      )}
    </div>
  );
}
